import React, { useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { Loader2, CheckCircle2, ArrowRight, MapPin, Mail, Clock } from "lucide-react";
import { Toaster, toast } from "sonner";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PROFILS = ["Sportif", "Entrepreneur / pro", "Étudiant", "Autre"];
const SERVICES = [
  "Coaching individuel",
  "Séance de groupe / atelier",
  "Visualisation / stress / confiance",
  "Accompagnement longue durée",
  "Je ne sais pas encore",
];

export const Contact = () => {
  const [form, setForm] = useState({
    nom: "",
    email: "",
    telephone: "",
    profil: "",
    service: "",
    creneau: "",
    message: "",
  });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.nom || !form.email || !form.profil || !form.message) {
      toast.error("Merci de compléter les champs obligatoires.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, form);
      setDone(true);
      toast.success("Demande envoyée ! Léa vous recontacte sous 48h.");
      setForm({ nom: "", email: "", telephone: "", profil: "", service: "", creneau: "", message: "" });
    } catch (err) {
      const msg = err?.response?.data?.detail?.[0]?.msg || "Une erreur est survenue. Réessayez.";
      toast.error(typeof msg === "string" ? msg : "Une erreur est survenue.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="contact" className="relative py-24 md:py-32 bg-[#FAF6F1]">
      <Toaster richColors position="top-center" />
      <div className="max-w-7xl mx-auto px-6 md:px-12 grid grid-cols-1 lg:grid-cols-12 gap-12">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="lg:col-span-5"
        >
          <span className="text-xs font-bold uppercase tracking-[0.2em] text-[#9B8FE0]">
            Contact
          </span>
          <h2 className="font-display mt-4 text-4xl md:text-5xl lg:text-6xl font-bold leading-[1.05] tracking-tight">
            Prêt·e&nbsp;? On en parle.
          </h2>
          <p className="mt-6 text-zinc-600 text-xl md:text-2xl leading-relaxed max-w-md">
            La première séance découverte est offerte (30 min, en visio). On
            définit ensemble votre objectif et le format qui vous correspond.
          </p>

          <div className="mt-10 space-y-5">
            <div className="flex items-start gap-4">
              <div className="w-11 h-11 rounded-2xl bg-zinc-900 text-white flex items-center justify-center"><Mail size={18} /></div>
              <div>
                <div className="text-base text-zinc-500">Email</div>
                <a className="font-semibold text-lg md:text-xl hover:text-[#9B8FE0]" href="mailto:perfbymind@gmail.com">perfbymind@gmail.com</a>
              </div>
            </div>
            <div className="flex items-start gap-4">
              <div className="w-11 h-11 rounded-2xl bg-zinc-900 text-white flex items-center justify-center"><MapPin size={18} /></div>
              <div>
                <div className="text-base text-zinc-500">Localisation</div>
                <div className="font-semibold text-lg md:text-xl">France · Visio &amp; présentiel</div>
              </div>
            </div>
            <div className="flex items-start gap-4">
              <div className="w-11 h-11 rounded-2xl bg-zinc-900 text-white flex items-center justify-center"><Clock size={18} /></div>
              <div>
                <div className="text-base text-zinc-500">Réponse</div>
                <div className="font-semibold text-lg md:text-xl">Sous 48h ouvrées</div>
              </div>
            </div>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.1 }}
          className="lg:col-span-7"
        >
          {done ? (
            <div data-testid="contact-success" className="bg-white border border-zinc-200 rounded-3xl p-10 md:p-14 text-center">
              <div className="w-16 h-16 rounded-full bg-[#9B8FE0]/10 mx-auto flex items-center justify-center">
                <CheckCircle2 className="text-[#9B8FE0]" size={32} />
              </div>
              <h3 className="font-display mt-6 text-3xl md:text-4xl font-bold">Demande bien reçue&nbsp;!</h3>
              <p className="mt-3 text-zinc-600 max-w-md mx-auto">
                Léa va prendre connaissance de votre message et vous recontacter
                personnellement sous 48h ouvrées.
              </p>
              <button
                onClick={() => setDone(false)}
                data-testid="contact-send-another"
                className="mt-8 inline-flex items-center gap-2 text-[#9B8FE0] font-bold"
              >
                Envoyer une autre demande <ArrowRight size={16} />
              </button>
            </div>
          ) : (
            <form
              onSubmit={submit}
              data-testid="contact-form"
              className="bg-white border border-zinc-200 rounded-3xl p-6 md:p-10 space-y-5"
            >
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <Field label="Nom complet *" id="nom">
                  <Input data-testid="contact-input-nom" id="nom" value={form.nom} onChange={(e) => update("nom", e.target.value)} className="pbm-input h-12 rounded-xl border-2" placeholder="Votre nom" required />
                </Field>
                <Field label="Email *" id="email">
                  <Input data-testid="contact-input-email" id="email" type="email" value={form.email} onChange={(e) => update("email", e.target.value)} className="pbm-input h-12 rounded-xl border-2" placeholder="vous@exemple.com" required />
                </Field>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <Field label="Téléphone" id="tel">
                  <Input data-testid="contact-input-tel" id="tel" value={form.telephone} onChange={(e) => update("telephone", e.target.value)} className="pbm-input h-12 rounded-xl border-2" placeholder="06 XX XX XX XX" />
                </Field>
                <Field label="Vous êtes *" id="profil">
                  <Select value={form.profil} onValueChange={(v) => update("profil", v)}>
                    <SelectTrigger data-testid="contact-select-profil" className="pbm-input h-12 rounded-xl border-2 px-3">
                      <SelectValue placeholder="Choisissez..." />
                    </SelectTrigger>
                    <SelectContent>
                      {PROFILS.map((p) => (
                        <SelectItem key={p} value={p} data-testid={`profil-opt-${p}`}>{p}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </Field>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <Field label="Service souhaité" id="service">
                  <Select value={form.service} onValueChange={(v) => update("service", v)}>
                    <SelectTrigger data-testid="contact-select-service" className="pbm-input h-12 rounded-xl border-2 px-3">
                      <SelectValue placeholder="Choisissez (facultatif)" />
                    </SelectTrigger>
                    <SelectContent>
                      {SERVICES.map((s) => (
                        <SelectItem key={s} value={s}>{s}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </Field>
                <Field label="Créneau souhaité" id="creneau">
                  <Input data-testid="contact-input-creneau" id="creneau" value={form.creneau} onChange={(e) => update("creneau", e.target.value)} className="pbm-input h-12 rounded-xl border-2" placeholder="Ex : mardi soir, semaine du 12..." />
                </Field>
              </div>

              <Field label="Votre message *" id="message">
                <Textarea
                  data-testid="contact-input-message"
                  id="message"
                  rows={5}
                  value={form.message}
                  onChange={(e) => update("message", e.target.value)}
                  className="pbm-input rounded-xl border-2 resize-none"
                  placeholder="Parlez-moi de votre objectif, votre contexte, vos contraintes..."
                  required
                />
              </Field>

              <button
                type="submit"
                disabled={loading}
                data-testid="contact-submit"
                className="w-full md:w-auto inline-flex items-center justify-center gap-3 bg-[#9B8FE0] hover:bg-[#8678D4] disabled:opacity-60 text-white font-bold py-4 px-10 rounded-full transition-all hover:-translate-y-0.5 hover:shadow-[0_12px_30px_-8px_rgba(255,79,0,0.5)]"
              >
                {loading ? <><Loader2 className="animate-spin" size={18} /> Envoi en cours...</> : <>Envoyer ma demande <ArrowRight size={18} /></>}
              </button>

              <p className="text-sm md:text-base text-zinc-500">
                En soumettant ce formulaire, vous acceptez d'être recontacté·e
                par Léa Mistral à l'email indiqué. Vos données ne sont pas
                partagées avec des tiers.
              </p>
            </form>
          )}
        </motion.div>
      </div>
    </section>
  );
};

const Field = ({ label, id, children }) => (
  <div className="space-y-2">
    <Label htmlFor={id} className="text-base md:text-lg font-semibold text-zinc-800">{label}</Label>
    {children}
  </div>
);

export default Contact;
